import { Dispatch, SetStateAction } from 'react';
import HelpTip from '../../../components/HelpTip';
import { LLMProvider, LLMRoleConfigMap } from '../../../types';
import { enabledLLMModels, parseLLMModels, selectableLLMProviders } from '../../../utils/llmRoleOverrides';

type RoleKey = keyof LLMRoleConfigMap;

interface RoleOption {
  key: RoleKey;
  label: string;
}

interface LLMRoleOverridesSectionProps {
  providers: LLMProvider[];
  roleOverrides: Partial<LLMRoleConfigMap>;
  roles: RoleOption[];
  title: string;
  titleTip: string;
  globalDefaultLabel: string;
  providerDisabledLabel: string;
  providerUnavailableLabel: string;
  selectModelLabel: string;
  emptyLabel: string;
  onRoleOverridesChange: Dispatch<SetStateAction<Partial<LLMRoleConfigMap>>>;
}

export default function LLMRoleOverridesSection({
  providers,
  roleOverrides,
  roles,
  title,
  titleTip,
  globalDefaultLabel,
  providerDisabledLabel,
  providerUnavailableLabel,
  selectModelLabel,
  emptyLabel,
  onRoleOverridesChange,
}: LLMRoleOverridesSectionProps) {
  const selectableProviders = selectableLLMProviders(providers);
  const hasStoredOverride = roles.some(role => roleOverrides[role.key] !== undefined);
  return (
    <div>
      {title && (
        <label className="form-label" style={{ marginBottom: '0.35rem' }}>
          {title}
          <HelpTip text={titleTip} />
        </label>
      )}
      {providers.length > 0 || hasStoredOverride ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {roles.map(role => {
            const override = roleOverrides[role.key];
            const providerId = override?.providerId || '';
            const model = override?.model || '';
            const selectedProvider = providers.find(provider => provider.id === providerId);
            const modelsForProvider = enabledLLMModels(selectedProvider);
            const currentProviderUnavailable = Boolean(providerId) && !selectableProviders.some(provider => provider.id === providerId);
            const currentModelInfo = selectedProvider && parseLLMModels(selectedProvider.models).find(item => item.name === model);
            const currentModelUnavailable = Boolean(model) && !modelsForProvider.some(item => item.name === model);

            return (
              <div key={role.key} style={{ display: 'grid', gridTemplateColumns: '6rem 1fr 1fr', gap: '0.4rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{role.label}</span>
                <select
                  className="input"
                  value={providerId}
                  onChange={event => {
                    const newProviderId = event.target.value;
                    onRoleOverridesChange(prev => {
                      const next = { ...prev };
                      if (!newProviderId) {
                        delete next[role.key];
                      } else {
                        const provider = providers.find(item => item.id === newProviderId);
                        const models = enabledLLMModels(provider);
                        next[role.key] = { providerId: newProviderId, model: models.length > 0 ? models[0].name : '' };
                      }
                      return next;
                    });
                  }}
                  style={{ fontSize: '0.8rem' }}
                >
                  <option value="">{globalDefaultLabel}</option>
                  {selectableProviders.map(provider => (
                    <option key={provider.id} value={provider.id}>{provider.name}</option>
                  ))}
                  {currentProviderUnavailable && (
                    <option value={providerId} disabled>
                      {selectedProvider?.name || providerId} {selectedProvider?.disabled ? providerDisabledLabel : providerUnavailableLabel}
                    </option>
                  )}
                </select>
                <select
                  className="input"
                  value={model}
                  onChange={event => {
                    const newModel = event.target.value;
                    onRoleOverridesChange(prev => ({
                      ...prev,
                      [role.key]: { providerId: prev[role.key]?.providerId ?? '', model: newModel },
                    }));
                  }}
                  disabled={!providerId}
                  style={{ fontSize: '0.8rem' }}
                >
                  <option value="" disabled={Boolean(providerId)}>{selectModelLabel}</option>
                  {modelsForProvider.map(item => (
                    <option key={item.name} value={item.name}>{item.displayName || item.name}</option>
                  ))}
                  {currentModelUnavailable && (
                    <option value={model} disabled>
                      {model} {currentModelInfo?.disabled ? providerDisabledLabel : providerUnavailableLabel}
                    </option>
                  )}
                </select>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', padding: '0.5rem 0' }}>
          {emptyLabel}
        </div>
      )}
    </div>
  );
}
